import * as React from 'react';
import { HoverCard, HoverCardTrigger, HoverCardContent } from '@/components/ui/hover-card';
import { isDevEnvironment, isPreviewEnvironment } from '@/utils/imageTransformationUtils';
import { ErrorBoundary } from '@/components/ui/error-boundary';

interface DebugTooltipProps {
  children: React.ReactNode;
  content: React.ReactNode;
  disabled?: boolean;
  className?: string;
}

/**
 * A tooltip component specifically for debug information in dev/preview environments
 * Enhanced with safety checks for content rendering
 */
export function DebugTooltip({ 
  children, 
  content, 
  disabled = false,
  className = ''
}: DebugTooltipProps) {
  const isDebugMode = isDevEnvironment() || isPreviewEnvironment();
  
  // Enhanced safety check for content rendering
  const renderSafeContent = () => {
    try {
      // Handle different content types safely
      if (React.isValidElement(content)) {
        return content;
      } else if (typeof content === 'string' || typeof content === 'number') {
        return <div>{String(content)}</div>;
      } else if (content && typeof content === 'object') {
        try {
          // Safely convert objects to strings
          const stringContent = JSON.stringify(content, null, 2);
          return <pre className="whitespace-pre-wrap text-xs">{stringContent}</pre>;
        } catch (err) {
          return <div className="text-red-400">Error displaying object content</div>;
        }
      } else if (content === null || content === undefined) {
        return <div className="text-gray-400">(No debug info)</div>;
      } else {
        return <div>{String(content)}</div>;
      }
    } catch (error) {
      console.error("Error rendering debug tooltip content:", error);
      return <div className="text-red-400">Error rendering debug content</div>;
    }
  };
  
  if (!isDebugMode || disabled) {
    return <>{children}</>;
  }
  
  return (
    <ErrorBoundary componentName="DebugTooltip">
      <HoverCard>
        <HoverCardTrigger asChild>
          <div className={`relative group ${className}`}>
            {children}
          </div>
        </HoverCardTrigger>
        <HoverCardContent
          align="end" 
          className="p-2 text-xs bg-black/90 text-white border-none max-w-[500px] overflow-auto max-h-[400px] z-[9999]"
        >
          {renderSafeContent()}
        </HoverCardContent>
      </HoverCard>
    </ErrorBoundary>
  );
}
